import { type RefObject, useEffect, useState } from 'react'

export interface Dimensions {
	width: number
	height: number
}

export interface UseDimensionsOptions {
	/** Debounce delay in ms (default: 0 = no debounce) */
	debounce?: number
	/** Use clientWidth/Height (default) or getBoundingClientRect */
	method?: 'client' | 'bounding'
	/** Initial dimensions before first measurement */
	initial?: Dimensions
}

// Feature detection for SSR compatibility
const hasResizeObserver = typeof ResizeObserver !== 'undefined'

/**
 * Track dimensions of a DOM element using ResizeObserver.
 *
 * @param ref - React ref to the element to observe
 * @param options - Configuration options
 * @returns Current dimensions { width, height }
 *
 * @example
 * const containerRef = useRef<HTMLDivElement>(null)
 * const { width, height } = useDimensions(containerRef)
 *
 * @example
 * // With debounce for performance
 * const dimensions = useDimensions(containerRef, { debounce: 100 })
 */
export function useDimensions(
	ref: RefObject<HTMLElement | null>,
	options: UseDimensionsOptions = {},
): Dimensions {
	const { debounce = 0, method = 'client', initial = { width: 0, height: 0 } } = options
	const [dimensions, setDimensions] = useState<Dimensions>(initial)

	useEffect(() => {
		if (!hasResizeObserver) return

		const element = ref.current
		if (!element) return

		let timeoutId: ReturnType<typeof setTimeout> | null = null

		const measure = () => {
			if (method === 'bounding') {
				const rect = element.getBoundingClientRect()
				setDimensions({ width: rect.width, height: rect.height })
			} else {
				setDimensions({ width: element.clientWidth, height: element.clientHeight })
			}
		}

		const handleResize = () => {
			if (debounce > 0) {
				if (timeoutId) clearTimeout(timeoutId)
				timeoutId = setTimeout(measure, debounce)
			} else {
				measure()
			}
		}

		// Initial measurement
		measure()

		// Observe for changes
		const observer = new ResizeObserver(handleResize)
		observer.observe(element)

		return () => {
			observer.disconnect()
			if (timeoutId) clearTimeout(timeoutId)
		}
	}, [ref, debounce, method])

	return dimensions
}
